import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from '../../utils/toast';
import { ArrowLeftIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const MasterDataDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [masterData, setMasterData] = useState(null);
  const [uploadFields, setUploadFields] = useState([]);

  useEffect(() => {
    fetchMasterData();
    fetchUploadFields();
  }, [id]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/master-data/${id}`);
      setMasterData(response.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to fetch master data details');
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadFields = async () => {
    try {
      const response = await api.get('/admin/upload-fields');
      setUploadFields(response.data);
    } catch (error) {
      console.error('Failed to fetch upload fields:', error);
    }
  };

  const handleEdit = () => {
    navigate(`/master-data/${id}/edit`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this master data record?')) {
      return;
    }

    try {
      await api.delete(`/master-data/${id}`);
      toast.success('Master data deleted successfully');
      navigate('/master-data');
    } catch (error) {
      console.error('Error deleting master data:', error);
      toast.error('Failed to delete master data');
    }
  };

  const getUploadFieldLabel = (fieldName) => {
    const field = uploadFields.find(f => f.name === fieldName);
    return field ? field.label : fieldName;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!masterData) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Master Data not found</h2>
        <p className="text-gray-600 mt-2">The requested master data record could not be found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/master-data')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">Master Data Details</h1>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleEdit}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Basic Information</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Client Name</label>
              <p className="mt-1 text-sm text-gray-900">{masterData.clientName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Client Type</label>
              <p className="mt-1 text-sm text-gray-900">{masterData.clientType?.name || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                masterData.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {masterData.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Created At</label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(masterData.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Client Address</label>
            <p className="mt-1 text-sm text-gray-900">{masterData.clientAddress || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Contact Persons */}
      {masterData.contactPersons && masterData.contactPersons.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Contact Persons</h2>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              {masterData.contactPersons.map((contact, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Title</label>
                      <p className="mt-1 text-sm text-gray-900">{contact.title}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <p className="mt-1 text-sm text-gray-900">{contact.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="mt-1 text-sm text-gray-900">{contact.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone Numbers</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {Array.isArray(contact.phoneNumbers) 
                          ? contact.phoneNumbers.join(', ') 
                          : contact.phoneNumbers}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Authorized Signatories */}
      {masterData.authorizedSignatories && masterData.authorizedSignatories.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Authorized Signatories</h2>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              {masterData.authorizedSignatories.map((signatory, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Title</label>
                      <p className="mt-1 text-sm text-gray-900">{signatory.title}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <p className="mt-1 text-sm text-gray-900">{signatory.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Age</label>
                      <p className="mt-1 text-sm text-gray-900">{signatory.age || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Designation</label>
                      <p className="mt-1 text-sm text-gray-900">{signatory.designation || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Designation Name</label>
                      <p className="mt-1 text-sm text-gray-900">{signatory.designationName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="mt-1 text-sm text-gray-900">{signatory.email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <p className="mt-1 text-sm text-gray-900">{signatory.phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Father's Name</label>
                      <p className="mt-1 text-sm text-gray-900">{signatory.fatherName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <p className="mt-1 text-sm text-gray-900">{signatory.address || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Documents */}
      {masterData.documents && Object.keys(masterData.documents).length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Documents</h2>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              {Object.entries(masterData.documents).map(([fieldName, document]) => (
                <div key={fieldName} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {getUploadFieldLabel(fieldName)}
                      </label>
                      <p className="mt-1 text-sm text-gray-900">{document.fileName}</p>
                      {document.uploadedAt && (
                        <p className="mt-1 text-xs text-gray-500">
                          Uploaded: {new Date(document.uploadedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="ml-4">
                      <a
                        href={document.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        View Document
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterDataDetail; 